import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

interface Stream {
  id: string;
  title: string;
  description: string | null;
  thumbnailUrl: string | null;
  priceCents: number;
  isLive: boolean;
  scheduledAt: string | null;
  viewerCount: number;
}

export default function StreamDetail() {
  const { id } = useParams<{ id: string }>();
  const [stream, setStream] = useState<Stream | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [thumbnailCacheBuster, setThumbnailCacheBuster] = useState(Date.now());

  useEffect(() => {
    fetchStream();

    // Poll for stream updates every 15 seconds to get fresh data
    const pollInterval = setInterval(() => {
      fetchStream();
    }, 15000);

    return () => clearInterval(pollInterval);
  }, [id]);

  // Cache buster for live thumbnail updates
  useEffect(() => {
    if (!stream?.isLive) return;

    const interval = setInterval(() => {
      setThumbnailCacheBuster(Date.now());
    }, 10000);

    return () => clearInterval(interval);
  }, [stream?.isLive]);

  async function fetchStream() {
    try {
      const res = await fetch(`/api/streams/${id}`);
      if (!res.ok) throw new Error('Stream not found');
      const data = await res.json();
      setStream(data);
    } catch (err) {
      setError('Stream not found');
    } finally {
      setLoading(false);
    }
  }

  async function handlePurchase(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !stream) return;

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/payments/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ streamId: stream.id, email })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to create checkout');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process payment');
      setSubmitting(false);
    }
  }

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  // Get thumbnail URL with cache busting for live streams
  const getThumbnailUrl = () => {
    if (!stream?.thumbnailUrl) return null;

    // Add cache buster for live streams to refresh thumbnail periodically
    if (stream.isLive && stream.thumbnailUrl.startsWith('/thumbnails/')) {
      return `${stream.thumbnailUrl}?t=${thumbnailCacheBuster}`;
    }

    return stream.thumbnailUrl;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!stream) {
    return (
      <div className="center-page">
        <div className="center-card">
          <h1>Stream Not Found</h1>
          <p>This stream doesn't exist or has been removed.</p>
          <Link to="/" className="btn btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Stream Preview */}
          <div className="card" style={{ marginBottom: '30px', overflow: 'hidden' }}>
            <div style={{
              aspectRatio: '16/9',
              background: 'var(--bg-input)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              {getThumbnailUrl() ? (
                <img
                  src={getThumbnailUrl()!}
                  alt={stream.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.5">
                  <polygon points="23 7 16 12 23 17 23 7"></polygon>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                </svg>
              )}
              {stream.isLive && (
                <span className="badge badge-live" style={{ position: 'absolute', top: '16px', left: '16px' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'white', display: 'inline-block' }}></span>
                  LIVE NOW
                </span>
              )}
            </div>
          </div>

          {/* Stream Info */}
          <div className="stream-detail-layout">
            <div className="stream-detail-info">
              <h1>{stream.title}</h1>

              {stream.isLive && stream.viewerCount > 0 && (
                <p className="viewer-count">
                  {stream.viewerCount} {stream.viewerCount === 1 ? 'viewer' : 'viewers'} watching now
                </p>
              )}

              {stream.description && (
                <div className="stream-description">
                  {stream.description.split('\n').map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              )}
            </div>

            {/* Purchase Form */}
            <div className="card stream-detail-checkout">
              <div style={{ marginBottom: '20px' }}>
                <span className="price">{formatPrice(stream.priceCents)}</span>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '4px' }}>
                  Unlimited access
                </p>
              </div>

              {error && <div className="message message-error">{error}</div>}

              <form onSubmit={handlePurchase}>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    className="form-input"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '8px' }}>
                    We'll send your access code here
                  </p>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  disabled={submitting || !email}
                >
                  {submitting ? 'Processing...' : `Buy Access - ${formatPrice(stream.priceCents)}`}
                </button>
              </form>

              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--border)' }}>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                  Already have a code?{' '}
                  <Link to={`/redeem?streamId=${stream.id}`}>Redeem here</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
